import TunnusCore from "../TunnusCore";
import EmbeddedAuthenticator from "./EmbeddedAuthenticator";
import SessionFunctions from "./SessionFunctions";
import InitCheck from "./InitCheck";
import LoginFunctions from "./LoginFunctions";
import localStore from "../storageAPIs/localStore";
import sessionStore from "../storageAPIs/sessionStore";
import { validateLoginForm, validateRegisterForm } from "../functions/Validators";
import trimFormData from "../functions/trimFormData";
import defaultConfig from "../defaultConfig";
import clearUrl from "./clearUrl";
import storageIsAvailable from "./checkers/storageIsAvailable";
import UserStore from "./user/UserStore";
import PubSub from "./user/PubSub";

let instance = null;
let lng = "fi";
const setLang = (lang) => {
  lng = lang || "fi";
};

const EmbeddedCore = (baseConfig) => {
  const config = { ...defaultConfig, ...baseConfig };
  const state = {
    isUniversal: false,
    tokenExpired: false,
    cookiesEnabled: false,
    storageIsAvailable: storageIsAvailable(),
  };

  config.s = config.s || config.source;
  config.storedSource = sessionStore.getSource();
  sessionStore.removeSource();

  const funcs = {};

  funcs.authenticator = EmbeddedAuthenticator(config);
  funcs.events = PubSub();
  funcs.userStore = UserStore(funcs);
  funcs.loginFunctions = LoginFunctions(config, funcs);
  funcs.sessionFunctions = SessionFunctions(config, state, funcs);

  const removeSession = () => localStore.removeIdTokenData();

  const clearUser = () => funcs.userStore.clearUser();

  const isFacebookApp = (ua = navigator.userAgent || navigator.vendor || window.opera) =>
    (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1) && (ua.indexOf("FBAN") > -1 || ua.indexOf("FBAV") > -1);

  const redirectUniversal = ({ modal, prompt, source } = {}) => {
    sessionStore.setOriginUrl(window.location.href);
    sessionStore.setSource(source || funcs.analytics.source);
    funcs.authenticator.redirectToUniversal({ lng, modal, prompt, source });
  };

  const initCheck = InitCheck(config, state, funcs);

  const runInitCheck = () => {
    if (config.runInitCheck !== false) initCheck.run();
  };

  return Object.assign({}, TunnusCore(config, state, funcs), {
    runInitCheck,
    clearUrl,
    setSource: funcs.analytics.setSource,
    analytics: funcs.analytics,
    env: state.env,
    storageIsAvailable: state.storageIsAvailable,
    isRedirectToHostedPage: () => !state.cookiesEnabled || isFacebookApp(),
    getCookiesEnabled: () => state.cookiesEnabled,
    getEvents: () => funcs.events,
    reset: () => {
      clearUser();
      removeSession();
    },
    login: async (username, password, suppressCallback = false) => {
      try {
        funcs.sessionFunctions.validate3rdPCEnabled();
        await validateLoginForm(username, password);
        const result = await funcs.loginFunctions.login(username, password);
        if (!suppressCallback) config.loginCallback(null, result);
        funcs.analytics.sendAuthenticationEvent(true, result.user, "login", "almatunnus-login");
        return { success: result, errors: null };
      } catch (error) {
        funcs.analytics.sendEvent("login", "failure-login", "almatunnus-login");
        if (!suppressCallback) config.loginCallback(error, null);
        return { success: null, errors: Array.isArray(error) ? error : [error] };
      }
    },
    logout: (rTo) => {
      removeSession();
      const returnTo = rTo || config.returnTo;
      if (config.analyticsWait) {
        return funcs.analytics.sendLogout(() => funcs.loginFunctions.logout(returnTo));
      }
      funcs.analytics.sendLogout();
      return funcs.loginFunctions.logout(returnTo);
    },
    registerAndLoginUser: async (formData) =>
      validateRegisterForm(trimFormData(formData))
        .then((data) => funcs.loginFunctions.registerAndLoginUser(data))
        .then((result) => {
          if (result.statusCode) funcs.analytics.sendRegistration(false);
          else {
            funcs.analytics.sendRegistration(true);
            funcs.analytics.sendAuthenticationEvent(true, result.user, "login", "almatunnus-login");
            config.loginCallback(null, result);
          }
          return result;
        })
        .catch((error) => {
          const errors = Array.isArray(error) ? error : [error];
          if (errors.find((e) => e.error === "consent")) {
            funcs.analytics.sendRegistration(false, true);
            if (errors.length > 1) funcs.analytics.sendRegistration(false);
          } else funcs.analytics.sendRegistration(false);
          return { errors };
        }),

    redirectToHostedLogin: ({ prompt, source, modal } = {}) => redirectUniversal({ prompt, source, modal }),

    redirectToHostedRegistration: ({ prompt, source } = {}) => redirectUniversal({ prompt, source, modal: "REGISTER" }),

    setSessionFromSilentLogin: async () => {
      try {
        const result = await funcs.loginFunctions.silentNoPromptLogin(true, true);
        funcs.analytics.sendAuthenticationEvent(true, result.user, "login-silent", "almatunnus-login");
        const originUrl = sessionStore.getOriginUrl();
        config.loginCallback(null, { ...result, originUrl });
        return result;
      } catch (error) {
        funcs.analytics.sendEvent("login-silent", "failure-login-silent", "almatunnus-login");
        return funcs.logger.log(error);
      }
    },

    checkSession: (allowSilent, simpleReturn) => funcs.sessionFunctions.checkSession(allowSilent, simpleReturn),

    checkExternalSession: (isRefresh = false, simpleReturn = true) =>
      state.cookiesEnabled ? funcs.sessionFunctions.checkExternalSession(isRefresh, simpleReturn) : false,

    getAccessToken: () => funcs.sessionFunctions.getAccessToken(),

    getIdToken: localStore.getIdTokenData,

    idTokenIsValid: async () => {
      const idTokenData = localStore.getIdTokenData();
      return idTokenData
        ? funcs.authenticator
            .verifyIdToken(idTokenData)
            .then(() => true)
            .catch(() => false)
        : null;
    },
    browserIsSupported: () => typeof window !== "undefined" && window.safari === undefined,

    getUser: funcs.userStore.getUser,

    getUpdatedUser: async () => {
      const idTokenData = localStore.getIdTokenData();
      return idTokenData
        ? funcs.authenticator
            .verifyIdToken(idTokenData)
            .then((idToken) => {
              funcs.userStore.setUser(idToken, false);
              return funcs.userStore.getUser();
            })
            .catch(() => false)
        : false;
    },

    cookiesEnabled: () => state.cookiesEnabled,
  });
};

export default (config) => {
  setLang(config.lng);
  if (instance) return instance;
  instance = EmbeddedCore(config);
  Object.seal(instance);
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    instance.runInitCheck();
    window.ALMA.tunnus = Object.assign(window.ALMA.tunnus, instance);
  }

  return instance;
};
